import * as yup from 'yup';
import { GetAllProjectTemplatesForProjectsAPI, GetDefaultTemplateForProject } from '../../../../../services/api/project-template-api';
import { AddProject, EditProjectNEWAPI, EditProjectWithTaskDueDateExtensionAPI, GetProjectsUsersAndGroups } from '../../../../../services/api/project-api';
import * as dynamicFieldsService from '../../../../../services/api/dynamic-fields-api';
import { FIELD_TYPES } from '../../../../../components/DynamicField/Tasks/utils/utils';

import { useCallback } from 'react';

export const validationSchema = (labels) => {
  return yup.object({
    Name: yup.string().trim().required(`${labels?.ProjectSingular ?? 'Project'} name is required`),
    status: yup.number().integer().moreThan(-1).lessThan(3).required("Status Required"),
    owner: yup.number().integer().moreThan(-1),
    hasClient: yup.boolean().required("Has Client Required"),
    clientName: yup.string().when('hasClient', { is: true, then: (schema) => schema.required("Client Name Required").typeError("Client Name Required"), otherwise: (schema) => schema.nullable() }),
    clientID: yup.string().nullable(),
    Start: yup.date().required('Start date is required').typeError('Start date must be a valid date'),
    End: yup.date().required('End date is required').typeError('End date must be a valid date').min(yup.ref('Start'), 'End date cannot be before Start date'),
  })
}

export const fetchCreateData = async ({
  user,
  setProjectTemplates,
  setSelectedOwner,
  setSelectedOwners,
  setProject,
  setLoaded,
  setAlerts,
  isMultiOwner,
  setIsMultiOwner,
}) => {
  try {
    const projectTemplatesResult = await GetAllProjectTemplatesForProjectsAPI();
    const templates = Array.isArray(projectTemplatesResult?.data)
      ? projectTemplatesResult.data
      : [];

      // const allTagsResult = await GetAllTags(); //TODO ensure this is calling the right API
      // setAllTags(allTagsResult);
      // console.log("All tags", allTagsResult);

    setProjectTemplates(
      templates.map((t) => ({
        value: t.Id,
        text: t.ProjectType,
        duration: t.Days,
        members: t.Members || [],
        owners: t.Owners || [],
        isMultiOwner: t.IsMultiOwner ?? true,
        canTransferOwner: t.CanTransferOwner !== false,
      }))
    );

    let allUsersResp = await GetProjectsUsersAndGroups();
    const filterMembers = (members = [], filter = "") =>
      members.filter((m) => (m?.Name || "").toLowerCase().includes(filter.toLowerCase()));
    const filteredUsers = filterMembers(allUsersResp?.data?.UserMembers, "");
    const filteredGroups = filterMembers(allUsersResp?.data?.GroupMembers, "");
    const allUsers = [...filteredUsers, ...filteredGroups];

    setProject((prev) => ({
      ...prev,
      allUsers,
    }));

    const defaultTemplate = await GetDefaultTemplate();
    const defaultIsMultiOwner = defaultTemplate?.Id ? (defaultTemplate?.IsMultiOwner ?? true) : true;

    if (setIsMultiOwner) {
      setIsMultiOwner(defaultIsMultiOwner);
    }

    applyTemplateDefaults({
      template: defaultTemplate,
      allUsers,
      currentUserId: user.userID,
      isMultiOwner: defaultIsMultiOwner,
      projectStartDate: new Date(),
      setProject,
      setSelectedOwner,
      setSelectedOwners,
      setFieldValue: () => {},
    });

    setLoaded(true);
  } catch (error) {
    setAlerts((prev) => ({ alertMessage: error.toString(), showAlert: true }));
    console.error("Error in fetchCreateData", error);
  }
};

// TAD-5898: builds the edit screen's member selection. Assigned members are
// intersected with the assignable pool, and EVERY assigned entry missing from
// the pool (archived, department-moved, deactivated, memberless group, ...) is
// appended as a display-only orphan — it stays selected and round-trips on
// save, so an unrelated project edit no longer removes it silently. The
// backend delete-and-recreates memberships from whatever the client sends;
// dropping an entry here permanently deletes it and cancels the member's
// tasks. Only Archived orphans render greyed (inactive members intentionally
// keep their normal styling); IsOrphaned keeps all of them out of the owner
// dropdowns.
export const buildEditMemberSelection = (project, userMembers = [], groupMembers = []) => {
  const dedupeById = (entries) =>
    entries.filter((entry, index) => entries.findIndex((e) => e.Id === entry.Id) === index);

  const assignedUsers = dedupeById(
    (project?.Members || [])
      .filter((m) => !(m.GroupId > 0) && m.User?.Id)
      .map((m) => m.User)
  );
  const assignedGroups = dedupeById([
    ...(project?.Members || []).filter((m) => m.GroupId > 0 && m.Group?.Id).map((m) => m.Group),
    ...(project?.Groups || []).filter((g) => g.Group?.Id).map((g) => g.Group),
  ]);

  const selectedUsers = userMembers.filter((user) => assignedUsers.some((member) => member.Id === user.Id));
  const selectedGroups = groupMembers.filter((group) => assignedGroups.some((member) => member.Id === group.Id));

  const orphanUsers = assignedUsers
    .filter((user) => !userMembers.some((p) => p.Id === user.Id))
    .map((user) => ({
      ...user,
      UserId: user.Id,
      CompanyName: user.CompanyName || user.Company?.Name || "",
      IsGroup: false,
      IsOrphaned: true,
    }));
  const orphanGroups = assignedGroups
    .filter((group) => !groupMembers.some((p) => p.Id === group.Id))
    .map((group) => ({
      ...group,
      GroupId: group.Id,
      IsGroup: true,
      IsOrphaned: true,
      Members: group.Members || [],
    }));

  return {
    allUsers: [...userMembers, ...groupMembers, ...orphanUsers, ...orphanGroups],
    members: [...selectedUsers, ...selectedGroups, ...orphanUsers, ...orphanGroups],
  };
};

export const useHandleFormChange = ({
  projectTemplates,
  setProject,
  project,
  setSelectedOwner,
  setSelectedOwners,
  isMultiOwner,
  setIsMultiOwner,
  setIsTransferModalOpen,
  setCanTransferOwner,
  currentUserId,
}) => {
  return useCallback(
    (field, value, setFieldValue) => {
      if (field === "ParentTemplateName") {
        const tpl = projectTemplates.find((t) => t.text === value);
        
        if (!tpl) {
          if (setIsMultiOwner) {
            setIsMultiOwner(true);
          }
          if (setCanTransferOwner) {
            setCanTransferOwner(true);
          }
          return;
        }

        setFieldValue("ParentTemplateId", tpl.value);
        setFieldValue("ParentTemplateName", tpl.text);

        setProject((prev) => ({
          ...prev,
          ParentTemplateId: tpl.value,
          ParentTemplateName: tpl.text,
        }));

        const templateIsMultiOwner = tpl.isMultiOwner ?? true;
        if (setIsMultiOwner) {
          setIsMultiOwner(templateIsMultiOwner);
        }

        const templateCanTransferOwner = tpl.canTransferOwner !== false;
        if (setCanTransferOwner) {
          setCanTransferOwner(templateCanTransferOwner);
        }

        applyTemplateDefaults({
          template: {
            Id: tpl.value,
            ProjectType: tpl.text,
            Days: tpl.duration,
            Members: tpl.members || [],
            Owners: tpl.owners || [],
          },
          allUsers: project?.allUsers || [],
          currentUserId,
          isMultiOwner: templateIsMultiOwner,
          projectStartDate: project?.Start ? new Date(project.Start) : new Date(),
          setProject,
          setSelectedOwner,
          setSelectedOwners,
          setFieldValue,
        });

        setIsTransferModalOpen?.(true);
        return;
      }

      if (field === "Owner") {
        if (isMultiOwner) {
          const raw = Array.isArray(value) ? value : [value];

          const ownerIds = [
            ...new Set(
              raw
                .map((o) => (typeof o === "object" && o ? o.Id : Number(o)))
                .filter((n) => Number.isFinite(n))
            ),
          ];

          const memberMap = new Map((project?.Members || []).map((m) => [m.Id, m]));
          const usersMap = new Map((project?.allUsers || []).map((u) => [u.Id, u]));
          ownerIds.forEach((oid) => {
            if (!memberMap.has(oid) && usersMap.has(oid)) {
              memberMap.set(oid, usersMap.get(oid));
            }
          });
          const newMembers = Array.from(memberMap.values());

          const ownerObjects = ownerIds
            .map((oid) => usersMap.get(oid))
            .filter(Boolean);

          const primaryOwnerId = ownerIds[0];

          setFieldValue("Owners", ownerIds);
          setFieldValue("Owner", primaryOwnerId);
          setFieldValue("OwnerId", primaryOwnerId);
          setFieldValue("Members", newMembers);

          setProject((prev) => ({
            ...prev,
            Owners: ownerIds,
            Owner: primaryOwnerId,
            OwnerId: primaryOwnerId,
            Members: newMembers,
          }));

          setSelectedOwners?.(ownerObjects);
          setSelectedOwner?.(null);
          setIsTransferModalOpen?.(true);
          return;
        }

        const users = project?.allUsers || [];
        const members = project?.Members || [];

        const ownerObj =
          (typeof value === "object" && value
            ? value
            : members.find((u) => u?.Name === value)) ||
              users.find((u) => u?.Name === value) ||
              null;

        const currentUser = users.find((u) => u?.Id === currentUserId && !u?.GroupId) || null;
        const finalOwnerObj = ownerObj || currentUser;
        const finalOwnerId = finalOwnerObj?.Id;

        if (finalOwnerId == null) {
          return;
        }

        const hasOwnerInMembers = members.some((m) => m.Id === finalOwnerId);
        const newMembers = hasOwnerInMembers ? members : [...members, finalOwnerObj];

        setFieldValue("Owner", finalOwnerId);
        setFieldValue("OwnerId", finalOwnerId);
        setFieldValue("Members", newMembers);

        setProject((prev) => ({
          ...prev,
          Owner: finalOwnerId,
          OwnerId: finalOwnerId,
          Members: newMembers,
        }));

        setSelectedOwner?.(finalOwnerObj);
        setIsTransferModalOpen?.(true);
        return;
      }

      setFieldValue(field, value);
      setProject((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    [ project, projectTemplates, setProject, setSelectedOwner, setSelectedOwners, isMultiOwner, setIsMultiOwner, setCanTransferOwner, setIsTransferModalOpen, currentUserId]
  );
};

export const setCancel = ({
    setPanelVisibility
}) => {
    setPanelVisibility((prev) => ({ ...prev, showCancelPanel: true }));
}

export const createProject = async ({
  project,
  setPanelVisibility,
  setAlerts,
}) => {
  try {
    if (project.Members.length === 0) {
        throw new Error("Project must have at least 1 member.");
    }
    if (project.Name.trim() === "") {
        throw new Error("Project name is required.");
    }
    if (project.Start === null || project.End === null) {
        throw new Error("Start and end date are required.");
    }
    const { allUsers, ...rest } = project;
    let obj = {
        ...rest,
        SelectedUsers: project.Members.map(member => {if(member.GroupId > 0) return member.GroupId+99999; else return member.Id}),
    };
    const result = await AddProject(obj);
    if (result.status !== 200) {
        throw new Error(result?.request?.response);
    }
    setPanelVisibility((prev) => ({ ...prev, showConfirmPanel: true }));
  } catch (error) {
      setAlerts((prev) => ({ 
          alertMessage: error?.toString().includes("Project With This Name Already Exists") ? error?.toString() : "Please Check for Duplicate Project Name and invalid Start/End Dates. " + error?.toString(),
          showAlert: true 
      }));
  }
}

export const editProject = async ({
    project,
    setPanelVisibility,
    setAlerts,
}) => {
    try {
        if (project.Members.length === 0) {
            throw new Error("Project must have at least 1 member.");
        }
        if (project.Name.trim() === "") {
            throw new Error("Project name is required.");
        }
        if (project.Start === null || project.End === null) {
            throw new Error("Start and end date are required.");
        }
        const { allUsers, ...rest } = project;
        let obj = {
            ...rest,
            SelectedUsers: project.Members.map(member => member.userID),
        };
        console.log("Edit project", obj);
        const result = await EditProjectNEWAPI(obj);
        if (!result || result.error || result.status !== 200) {
  
            throw new Error("Error creating project");
        }
        setPanelVisibility((prev) => ({ ...prev, showConfirmPanel: true }));
    } catch (error) {
        setAlerts((prev) => ({ 
            alertMessage: error.toString(), 
            showAlert: true 
        }));
    }
}

export const editProjectWithTaskDueDateExtension = async ({
    project,
    setPanelVisibility,
    setAlerts,
}) => {
    try {
        if (project.Members.length === 0) {
            throw new Error("Project must have at least 1 member.");
        }
        if (project.Name.trim() === "") {
            throw new Error("Project name is required.");
        }
        if (project.Start === null || project.End === null) {
            throw new Error("Start and end date are required.");
        }
        const { allUsers, ...rest } = project;
        let obj = {
            ...rest,
            SelectedUsers: project.Members.map(member => member.userID),
        };
        const result = await EditProjectWithTaskDueDateExtensionAPI(obj);
        if (!result || result.error || result.status !== 200) {

            throw new Error("Error editing project");
        }
        setPanelVisibility((prev) => ({ ...prev, showConfirmPanel: true }));
    } catch (error) {
        setAlerts((prev) => ({
            alertMessage: error.toString(),
            showAlert: true
        }));
    }
}

export const getProjectDynamicFields = async (DynamicFieldRequestDto) => {
    try {
        const dynamicFields = await dynamicFieldsService.getDynamicFields(DynamicFieldRequestDto);
        return dynamicFields.map((field) => {
            const { FieldType, Data } = field;
            const parsedData =
                FieldType === FIELD_TYPES.date && Data != null && Data !== ''
                    ? new Date(Data)
                    : Data;
            return {
                ...field,
                Data: parsedData ?? '',
            };
        });
    } catch (error) {
        console.error(error);
        return [];
    }
}

export const GetDefaultTemplate = async () => {
    let result = await GetDefaultTemplateForProject();
    if (result.status === 200) {
        return result.data;
    }
    return {Id: '', ProjectType: ''};
}

export const pickUsersByIds = (ids = [], allUsers = []) => {
  const idSet = new Set((ids || []).filter((x) => Number.isFinite(Number(x))));
  const seen = new Set();
  const picked = [];
  for (const u of allUsers) {
    const id = Number(u?.Id ?? u?.UserId);
    if (!Number.isFinite(id)) continue;
    if (!idSet.has(id)) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    picked.push({ ...u, Id: id });
  }
  return picked;
};

export const buildTemplateDefaults = ({
  templateMembersIds = [],
  templateOwnerIds = [],
  allUsers = [],
  currentUserId,
  isMultiOwner = false,
}) => {

  const ownerIds =
    Array.isArray(templateOwnerIds) && templateOwnerIds.length > 0
      ? templateOwnerIds.map(Number).filter(Number.isFinite)
      : [currentUserId];
  const memberIds = new Set([
    ...((templateMembersIds || []).map(Number).filter(Number.isFinite)),
    ...ownerIds,
    currentUserId,
  ]);

  const members = pickUsersByIds([...memberIds], allUsers);

  if (isMultiOwner) {
    const owners = [...new Set(ownerIds)];
    const primaryOwnerId = owners[0] ?? currentUserId;
    return {
      members,
      owners,
      ownerId: primaryOwnerId
    };
  } else {
    const ownerId = ownerIds[0] ?? currentUserId;
    return {
      members,
      ownerId,
    };
  }
};

export const applyTemplateDefaults = ({
  template,
  allUsers = [],
  currentUserId,
  isMultiOwner = false,
  projectStartDate,
  setProject,
  setSelectedOwner,
  setSelectedOwners,
  setFieldValue,
}) => {
  const safeSetField = (name, value) => {
    if (typeof setFieldValue === "function") setFieldValue(name, value);
  };

  const tplMemberIds = Array.isArray(template?.Members) ? template.Members : [];
  const tplOwnerIds  = Array.isArray(template?.Owners)  ? template.Owners  : [];

  const ownerIdsInAllUsers = tplOwnerIds.filter((oid) =>
    (allUsers || []).some((u) => u.Id === oid)
  );
  const ownerIds = ownerIdsInAllUsers.length > 0
    ? ownerIdsInAllUsers
    : [currentUserId];

  const memberIdSet = new Set([...(tplMemberIds || []), ...ownerIds, currentUserId]);

  let members = (allUsers || []).filter((u) => memberIdSet.has(u?.Id));

  if (members.length === 0) {
    const meObj = (allUsers || []).find((u) => u.Id === currentUserId);
    if (meObj) members = [meObj];
  }

  const primaryOwnerId = ownerIds[0] ?? currentUserId;

  const ownerObj =
    (allUsers || []).find((u) => !u?.GroupId && u?.Id === primaryOwnerId) ||
    (allUsers || []).find((u) => u.Id === currentUserId && !u?.GroupId) ||
    null;

  let ownerObjects = (allUsers || []).filter(
    (u) => !u?.GroupId && ownerIds.includes(u?.Id)
  );
  if (ownerObjects.length === 0) {
    const meObj = (allUsers || []).find((u) => u.Id === currentUserId && !u?.GroupId);
    if (meObj) ownerObjects = [meObj];
  }
  const ownerIdsFinal = ownerObjects.map((u) => u.Id);
  const primaryOwnerIdFinal = ownerIdsFinal[0] ?? currentUserId;

  if (!members.some((m) => m.Id === primaryOwnerIdFinal)) {
    const ownerRef = (allUsers || []).find((u) => u.Id === primaryOwnerIdFinal);
    if (ownerRef) members = [...members, ownerRef];
  }

  const start = projectStartDate instanceof Date ? projectStartDate : new Date();
  const days = template?.Days ?? 0;
  const newEnd = new Date(start);
  newEnd.setDate(start.getDate() + days);

  safeSetField("Members", members);
  safeSetField("End", newEnd);

  const ownerDtos = ownerIdsFinal.map(id => ({Id: id, IsGroup: false}));

  if (isMultiOwner) {
    safeSetField("Owners", ownerDtos);
    safeSetField("Owner", primaryOwnerIdFinal);
    safeSetField("OwnerId", primaryOwnerIdFinal);
    setSelectedOwners?.(ownerObjects);
    setSelectedOwner?.(null);
  } else {
    safeSetField("Owner", primaryOwnerIdFinal);
    safeSetField("OwnerId", primaryOwnerIdFinal);
    setSelectedOwner?.(
      ownerObj ||
      (allUsers || []).find((u) => u.Id === currentUserId && !u?.GroupId) ||
      null
    );
    setSelectedOwners?.([]);
  }

  setProject?.((prev) => ({
    ...prev,
    Members: members,
    End: newEnd,
    ...(isMultiOwner
      ? { Owners: ownerDtos, Owner: primaryOwnerIdFinal, OwnerId: primaryOwnerIdFinal }
      : { Owners: [], Owner: primaryOwnerIdFinal, OwnerId: primaryOwnerIdFinal }),
  }));

  return {
    members,
    ownerId: primaryOwnerIdFinal,
    owners: isMultiOwner ? ownerDtos : undefined,
    end: newEnd,
  };
};
